const apiUrl = process.env.REACT_APP_API_URL || 'https://api.absat-erp.com';

const CURRENT_APP_NAME = process.env.REACT_APP_NAME || 'sales';

const normalizeValue = (value) => String(value || '').trim().toLowerCase();

const getCurrentHost = () => normalizeValue(window?.location?.hostname);

const getHostnameFromBaseUrl = (value) => {
  const normalized = String(value || '').trim();
  if (!normalized) return '';
  try {
    return normalizeValue(new URL(normalized).hostname);
  } catch (e) {
    return normalizeValue(normalized);
  }
};

const getAppCacheKey = () => {
  const host = getCurrentHost() || 'unknown-host';
  const appName = normalizeValue(CURRENT_APP_NAME) || 'unknown-app';
  return `currentAppId:${host}:${appName}`;
};

const getAppCacheTsKey = () => {
  const host = getCurrentHost() || 'unknown-host';
  const appName = normalizeValue(CURRENT_APP_NAME) || 'unknown-app';
  return `appIdTimestamp:${host}:${appName}`;
};

const getAppsArray = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.apps)) return payload.apps;
  if (Array.isArray(payload?.data?.apps)) return payload.data.apps;
  return [];
};

const resolveCurrentApp = (apps) => {
  const targetName = normalizeValue(CURRENT_APP_NAME);
  const currentHost = getCurrentHost();
  const hostWithoutWww = currentHost.replace(/^www\./, '');

  const scoreApp = (app) => {
    let score = 0;
    const appNameCandidates = [
      app?.name,
      app?.titleEn,
      app?.titleAr,
      app?.descEn,
      app?.descAr
    ]
      .map(normalizeValue)
      .filter(Boolean);

    const appBaseHost = getHostnameFromBaseUrl(app?.baseUrl);
    const appBaseHostWithoutWww = appBaseHost.replace(/^www\./, '');

    if (targetName && appNameCandidates.includes(targetName)) score += 100;
    if (targetName && appNameCandidates.some((value) => value.includes(targetName))) score += 30;
    if (currentHost && appBaseHost && currentHost === appBaseHost) score += 120;
    if (hostWithoutWww && appBaseHostWithoutWww && hostWithoutWww === appBaseHostWithoutWww) score += 90;
    if (currentHost && appBaseHost && (currentHost.includes(appBaseHost) || appBaseHost.includes(currentHost))) score += 40;
    if (app?.status === 'done') score += 5;

    return score;
  };

  let bestApp = null;
  let bestScore = -1;
  apps.forEach((app) => {
    const score = scoreApp(app);
    if (score > bestScore) {
      bestScore = score;
      bestApp = app;
    }
  });

  return bestScore > 0 ? bestApp : null;
};

const getPermissionsCacheKey = (userId) => (userId ? `userPermissions:${userId}` : 'userPermissions');
const getPermissionsCacheTsKey = (userId) => (userId ? `userPermissionsTimestamp:${userId}` : 'userPermissionsTimestamp');

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const getCurrentAppId = async () => {
  try {
    const appCacheKey = getAppCacheKey();
    const appCacheTsKey = getAppCacheTsKey();
    const cachedAppId = localStorage.getItem(appCacheKey) || localStorage.getItem('currentAppId');
    const cacheTimestamp = localStorage.getItem(appCacheTsKey) || localStorage.getItem('appIdTimestamp');
    const cacheAge = Date.now() - parseInt(cacheTimestamp || '0');

    if (cachedAppId && cacheAge < 5 * 60 * 1000) {
      return cachedAppId;
    }

    const response = await fetch(`${apiUrl}/auth/app`, {
      headers: getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const rawPayload = await response.json();
    const apps = getAppsArray(rawPayload);

    if (!Array.isArray(apps)) {
      console.error('Apps response is invalid:', rawPayload);
      return cachedAppId || null;
    }

    const currentApp = resolveCurrentApp(apps);

    if (currentApp && currentApp._id) {
      localStorage.setItem(appCacheKey, currentApp._id);
      localStorage.setItem(appCacheTsKey, Date.now().toString());
      localStorage.setItem('currentAppId', currentApp._id);
      localStorage.setItem('appIdTimestamp', Date.now().toString());
      return currentApp._id;
    } else {
      console.warn(`App not found for name: ${CURRENT_APP_NAME}`);
      return cachedAppId || null;
    }
  } catch (error) {
    console.error('Error getting current app ID:', error);
    const cachedAppId = localStorage.getItem(getAppCacheKey()) || localStorage.getItem('currentAppId');
    if (cachedAppId) {
      return cachedAppId;
    }

    return null;
  }
};

// forceRefresh: when true, bypass local cache to reflect role/scope changes immediately
export const fetchUserPermissions = async (userId, forceRefresh = false) => {
  try {
    const cacheKey = getPermissionsCacheKey(userId);
    const cacheTsKey = getPermissionsCacheTsKey(userId);

    // التحقق من الـ cache أولاً
    const cachedPermissions = localStorage.getItem(cacheKey);
    const cacheTimestamp = localStorage.getItem(cacheTsKey);
    const cacheAge = Date.now() - parseInt(cacheTimestamp || '0');
    
    // إذا كان الـ cache حديث (أقل من 5 دقائق)، استخدمه
    if (!forceRefresh && cachedPermissions && cacheAge < 5 * 60 * 1000) {
      return JSON.parse(cachedPermissions);
    }

    const response = await fetch(`${apiUrl}/auth/permissions/user/${userId}`, { headers: getAuthHeaders() });
    
    if (!response.ok) {
      if (response.status === 404) {
        console.warn('No permissions found for user');
        const emptyPermissions = [];
        localStorage.setItem(cacheKey, JSON.stringify(emptyPermissions));
        localStorage.setItem(cacheTsKey, Date.now().toString());
        return emptyPermissions;
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    const permissions = data.data?.apps || data.apps || [];
    
    // حفظ الصلاحيات في localStorage
    localStorage.setItem(cacheKey, JSON.stringify(permissions));
    localStorage.setItem(cacheTsKey, Date.now().toString());
    
    return permissions;
  } catch (error) {
    console.error('Error fetching user permissions:', error);
    
    // استخدام الـ cache كـ fallback
    const cacheKey = getPermissionsCacheKey(userId);
    const cachedPermissions = localStorage.getItem(cacheKey) || localStorage.getItem('userPermissions');
    if (cachedPermissions) {
      return JSON.parse(cachedPermissions);
    }
    
    return [];
  }
};

export const getAppPermission = (appId, permissions = null) => {
  try {
    // إذا لم يتم تمرير permissions، جلبها من localStorage
    if (!permissions) {
      const userStr = localStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : null;
      const cacheKey = getPermissionsCacheKey(user?._id);

      const storedPermissions =
        localStorage.getItem(cacheKey) ??
        localStorage.getItem('userPermissions');

      permissions = storedPermissions ? JSON.parse(storedPermissions) : [];
    }

    if (!appId) {
      console.warn('No appId provided to getAppPermission');
      return null;
    }

    // التأكد من أن permissions هو array
    if (!Array.isArray(permissions)) {
      console.error('Permissions is not an array:', permissions);
      return null;
    }

    // البحث عن صلاحية التطبيق
    const appPermission = permissions.find(p => {
      if (!p || !p.appId) return false;
      const permAppId = typeof p.appId === 'object' ? p.appId._id : p.appId;
      return permAppId === appId;
    });

    return appPermission || null;
  } catch (error) {
    console.error('Error getting app permission:', error);
    return null;
  }
};

export const filterByUserPermissions = (data, appId, filterMode = null, options = {}) => {
  try {
    if (!data || !Array.isArray(data) || data.length === 0) {
      return [];
    }

    const userStr = localStorage.getItem('user');
    if (!userStr) {
      console.error('[PermFilter] User not found in localStorage');
      return [];
    }

    const user = JSON.parse(userStr);
    if (!user || !user._id) {
      console.error('[PermFilter] Invalid user data in localStorage');
      return [];
    }

    const userId = user._id;

    // جلب صلاحيات التطبيق
    const appPermission = getAppPermission(appId);

    // إذا لم توجد صلاحية، نرجع array فارغة
    if (!appPermission) {
      console.warn('[PermFilter] No permission found for current app');
      return [];
    }

    const accessScope = appPermission.accessScope;
    const effectiveScope = filterMode || accessScope;


    // إذا كان own أو تم اختيار own من الفلتر
    if (effectiveScope === 'own') {
      const normalizeIdList = (value) => {
        if (!value) return [];
        if (Array.isArray(value)) {
          return value
            .map((v) => (typeof v === 'object' && v ? (v._id ?? v.id) : v))
            .filter(Boolean)
            .map((v) => String(v));
        }
        if (typeof value === 'object') {
          const v = value._id ?? value.id;
          return v ? [String(v)] : [];
        }
        if (typeof value === 'string') {
          const trimmed = value.trim();
          if (!trimmed) return [];
          try {
            const parsed = JSON.parse(trimmed);
            if (Array.isArray(parsed)) return normalizeIdList(parsed);
            if (parsed && typeof parsed === 'object') return normalizeIdList(parsed);
          } catch (e) {
            // ignore
          }
          if (trimmed.includes(',')) {
            return trimmed
              .split(',')
              .map((part) => part.replace(/[[\]"\\\s]/g, ''))
              .filter(Boolean);
          }
          return [trimmed.replace(/[[\]"\\\s]/g, '')].filter(Boolean);
        }
        return [String(value)];
      };

      const getOwnerId = (item) => {
        const candidate =
          item?.created_by ??
          item?.created_by_user_id ??
          item?.created_by_userId ??
          item?.created_by_user ??
          item?.createdByRaw ??
          item?.created_by_raw ??
          item?.createdBy ??
          item?.createdById ??
          item?.user_id ??
          item?.userId ??
          item?.owner_id ??
          item?.ownerId;

        // Handle ObjectId as object
        if (candidate && typeof candidate === 'object') {
          return candidate._id ?? candidate.id ?? null;
        }

        // Handle ObjectId as string or direct value
        return candidate ?? null;
      };

      const candidateUserSalesmanIds =
        options?.salesmanIds ??
        user?.salesman_ids ??
        user?.salesmanIds ??
        user?.salesman_id ??
        user?.salesmanId;

      const allowedSalesmanIds = new Set(normalizeIdList(candidateUserSalesmanIds));

      const getItemSalesmanIds = (item) => {
        const candidate =
          item?.salesman_id ??
          item?.salesman_ids ??
          item?.salesmanIds ??
          item?.salesmanId ??
          item?.salesman;
        return normalizeIdList(candidate);
      };

      const filtered = data.filter(item => {
        const ownerId = getOwnerId(item);
        
        // Convert both to string for comparison (in case one is ObjectId type)
        const ownerIdStr = ownerId ? String(ownerId) : null;
        const userIdStr = userId ? String(userId) : null;
        
        const ownerMatch = ownerIdStr === userIdStr;
        let assignedMatch = false;
        if (!ownerMatch && allowedSalesmanIds.size) {
          const itemSalesmanIds = getItemSalesmanIds(item);
          assignedMatch = itemSalesmanIds.some((id) => allowedSalesmanIds.has(String(id)));
        }

        const match = ownerMatch || assignedMatch;
        
        // Debug log for first few items
        if (data.indexOf(item) < 3) {
        }
        
        return match;
      });


      return filtered;
    }

    // إذا كان all، نرجع كل البيانات
    return data;

  } catch (error) {
    console.error('[PermFilter] Error in filterByUserPermissions:', error);
    return data;
  }
};

export const canShowFilterToggle = (appId) => {
  try {
    if (!appId) return false;
    const appPermission = getAppPermission(appId);
    return appPermission?.accessScope === 'all';
  } catch (error) {
    console.error('Error in canShowFilterToggle:', error);
    return false;
  }
};

export const hasPermission = (appId, action = 'view') => {
  try {
    if (!appId) return false;
    
    const appPermission = getAppPermission(appId);
    
    if (!appPermission) return false;

    const { userRole } = appPermission;

    // صلاحيات حسب الدور
    const permissions = {
      admin: ['view', 'create', 'edit', 'delete'],
      supervisor: ['view', 'create', 'edit'],
      user: ['view', 'create']
    };

    return permissions[userRole]?.includes(action) || false;
  } catch (error) {
    console.error('Error checking permission:', error);
    return false;
  }
};

export const clearPermissionsCache = () => {
  localStorage.removeItem('userPermissions');
  localStorage.removeItem('userPermissionsTimestamp');
  try {
    const userStr = localStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : null;
    if (user?._id) {
      localStorage.removeItem(getPermissionsCacheKey(user._id));
      localStorage.removeItem(getPermissionsCacheTsKey(user._id));
    }
  } catch (e) {
  }
  localStorage.removeItem(getAppCacheKey());
  localStorage.removeItem(getAppCacheTsKey());
  localStorage.removeItem('currentAppId');
  localStorage.removeItem('appIdTimestamp');
};
